<template>
  <div class="container-fluid">
    <b-overlay :show="isLoading">
      <!-- Job Details Card -->
      <b-card class="mb-2">
        <b-card-header>
          <div class="d-flex justify-content-between align-items-center w-100">
            <div>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="flat-secondary"
                size="sm"
                class="btn-icon mr-1"
                @click="goBack"
              >
                <feather-icon icon="ArrowLeftIcon" />
              </b-button>
              <b-card-title class="d-inline">
                {{ jobDetails.position }}
              </b-card-title>
            </div>
            <div>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :variant="jobDetails.isSaved ? 'warning' : 'outline-warning'"
                size="sm"
                class="mr-1"
                @click="toggleSaveJob"
              >
                <feather-icon
                  icon="StarIcon"
                  class="mr-50"
                />
                <span class="align-middle">{{ jobDetails.isSaved ? 'Saved' : 'Save Job' }}</span>
              </b-button>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                size="sm"
                @click="expressInterest"
              >
                <feather-icon
                  icon="SendIcon"
                  class="mr-50"
                />
                <span class="align-middle">Express Interest</span>
              </b-button>
            </div>
          </div>
        </b-card-header>
        <b-card-body>
          <b-row>
            <!-- Job Information -->
            <b-col
              md="8"
              cols="12"
            >
              <h5 class="mb-1">
                Job Details
              </h5>
              <b-row>
                <b-col
                  md="6"
                  cols="12"
                  class="mb-1"
                >
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="BriefcaseIcon"
                      class="mr-1"
                      size="18"
                    />
                    <div>
                      <small class="text-muted d-block">Company</small>
                      <span>{{ jobDetails.company }}</span>
                    </div>
                  </div>
                </b-col>
                <b-col
                  md="6"
                  cols="12"
                  class="mb-1"
                >
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="MapPinIcon"
                      class="mr-1"
                      size="18"
                    />
                    <div>
                      <small class="text-muted d-block">Location</small>
                      <span>{{ jobDetails.location }}</span>
                    </div>
                  </div>
                </b-col>
                <b-col
                  md="6"
                  cols="12"
                  class="mb-1"
                >
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="DollarSignIcon"
                      class="mr-1"
                      size="18"
                    />
                    <div>
                      <small class="text-muted d-block">Salary Range</small>
                      <span>{{ $formatCurrency(jobDetails.salaryMin) }} - {{ $formatCurrency(jobDetails.salaryMax) }}</span>
                    </div>
                  </div>
                </b-col>
                <b-col
                  md="6"
                  cols="12"
                  class="mb-1"
                >
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="ClockIcon"
                      class="mr-1"
                      size="18"
                    />
                    <div>
                      <small class="text-muted d-block">Employment Type</small>
                      <span>{{ jobDetails.employmentType }}</span>
                    </div>
                  </div>
                </b-col>
                <b-col
                  md="6"
                  cols="12"
                  class="mb-1"
                >
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="CalendarIcon"
                      class="mr-1"
                      size="18"
                    />
                    <div>
                      <small class="text-muted d-block">Date Posted</small>
                      <span>{{ $formatDate(jobDetails.datePosted) }}</span>
                    </div>
                  </div>
                </b-col>
                <b-col
                  md="6"
                  cols="12"
                  class="mb-1"
                >
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="AwardIcon"
                      class="mr-1"
                      size="18"
                    />
                    <div>
                      <small class="text-muted d-block">Experience Required</small>
                      <span>{{ jobDetails.experienceYears }} years</span>
                    </div>
                  </div>
                </b-col>
              </b-row>

              <hr class="my-2">

              <h5 class="mb-1">
                Job Description
              </h5>
              <p v-html="jobDetails.description" />

              <h5 class="mb-1 mt-2">
                Requirements
              </h5>
              <ul>
                <li
                  v-for="(requirement, index) in jobDetails.requirements"
                  :key="index"
                >
                  {{ requirement }}
                </li>
              </ul>

              <h5 class="mb-1 mt-2">
                Responsibilities
              </h5>
              <ul>
                <li
                  v-for="(responsibility, index) in jobDetails.responsibilities"
                  :key="index"
                >
                  {{ responsibility }}
                </li>
              </ul>
            </b-col>

            <!-- Agency Information Sidebar -->
            <b-col
              md="4"
              cols="12"
            >
              <b-card class="bg-light">
                <h5 class="mb-2">
                  Posted By
                </h5>
                <div class="d-flex align-items-center mb-2">
                  <b-avatar
                    :src="jobDetails.agencyLogo"
                    :text="jobDetails.agency.charAt(0)"
                    variant="light-primary"
                    size="48"
                    class="mr-1"
                  />
                  <div>
                    <h6 class="mb-0">
                      {{ jobDetails.agency }}
                    </h6>
                    <small class="text-muted">Recruitment Agency</small>
                  </div>
                </div>

                <hr>

                <div class="mb-1">
                  <small class="text-muted d-block">Contact Person</small>
                  <span>{{ jobDetails.contactPerson }}</span>
                </div>
                <div class="mb-1">
                  <small class="text-muted d-block">Email</small>
                  <span>{{ jobDetails.contactEmail }}</span>
                </div>
                <div class="mb-1">
                  <small class="text-muted d-block">Phone</small>
                  <span>{{ jobDetails.contactPhone }}</span>
                </div>

                <hr>

                <div class="mb-1">
                  <b-badge
                    :variant="getStatusVariant(jobDetails.status)"
                    pill
                    class="mr-50"
                  >
                    {{ jobDetails.status }}
                  </b-badge>
                </div>

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="outline-primary"
                  block
                  size="sm"
                  class="mt-2"
                  @click="contactAgency"
                >
                  <feather-icon
                    icon="PhoneIcon"
                    class="mr-50"
                  />
                  Contact Agency
                </b-button>
              </b-card>

              <!-- Commission Information -->
              <b-card class="mt-2 bg-light-success">
                <h6 class="mb-1">
                  <feather-icon
                    icon="TrendingUpIcon"
                    class="mr-50"
                  />
                  Brokerage Commission
                </h6>
                <h4 class="text-success mb-0">
                  {{ jobDetails.commissionPercentage }}%
                </h4>
                <small class="text-muted">of first month salary</small>
              </b-card>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Matched Candidates Card -->
      <b-card>
        <b-card-header>
          <b-card-title>
            Matched Candidates from Your Database
          </b-card-title>
          <div>
            <b-badge
              variant="light-success"
              pill
              class="font-medium-2"
            >
              {{ matchedCandidates.length }} Matches
            </b-badge>
          </div>
        </b-card-header>
        <b-card-body>
          <b-alert
            v-if="matchedCandidates.length === 0"
            variant="info"
            show
          >
            <div class="alert-body">
              <feather-icon
                icon="InfoIcon"
                class="mr-50"
              />
              No candidates from your database match this job's requirements yet.
            </div>
          </b-alert>

          <b-row v-else>
            <b-col
              v-for="candidate in matchedCandidates"
              :key="candidate.id"
              md="6"
              cols="12"
              class="mb-2"
            >
              <b-card class="candidate-card">
                <div class="d-flex align-items-start">
                  <b-avatar
                    :src="candidate.avatar"
                    :text="candidate.name.charAt(0)"
                    variant="light-primary"
                    size="64"
                    class="mr-2"
                  />
                  <div class="flex-grow-1">
                    <h5 class="mb-50">
                      {{ candidate.name }}
                    </h5>
                    <p class="mb-50 text-muted">
                      {{ candidate.currentPosition }}
                    </p>
                    <div class="mb-1">
                      <b-badge
                        variant="light-success"
                        class="mr-50"
                      >
                        {{ candidate.matchScore }}% Match
                      </b-badge>
                      <b-badge
                        v-if="candidate.isAvailable"
                        variant="light-info"
                      >
                        Available
                      </b-badge>
                    </div>

                    <div class="mb-1">
                      <small class="text-muted d-block">Experience</small>
                      <span>{{ candidate.experienceYears }} years</span>
                    </div>

                    <div class="mb-1">
                      <small class="text-muted d-block">Expected Salary</small>
                      <span>{{ $formatCurrency(candidate.expectedSalary) }}</span>
                    </div>

                    <div class="mb-1">
                      <small class="text-muted d-block">Key Skills</small>
                      <div class="d-flex flex-wrap">
                        <b-badge
                          v-for="(skill, index) in candidate.skills.slice(0, 3)"
                          :key="index"
                          variant="light-primary"
                          class="mr-50 mb-50"
                        >
                          {{ skill }}
                        </b-badge>
                        <b-badge
                          v-if="candidate.skills.length > 3"
                          variant="light-secondary"
                          class="mb-50"
                        >
                          +{{ candidate.skills.length - 3 }} more
                        </b-badge>
                      </div>
                    </div>

                    <div class="d-flex mt-1">
                      <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="outline-primary"
                        size="sm"
                        class="mr-1"
                        @click="viewCandidateProfile(candidate)"
                      >
                        <feather-icon
                          icon="EyeIcon"
                          class="mr-50"
                        />
                        View Profile
                      </b-button>
                      <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        size="sm"
                        @click="submitCandidate(candidate)"
                      >
                        <feather-icon
                          icon="SendIcon"
                          class="mr-50"
                        />
                        Submit
                      </b-button>
                    </div>
                  </div>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </b-overlay>

    <!-- Express Interest Modal -->
    <b-modal
      v-model="showInterestModal"
      title="Express Interest"
      hide-footer
      centered
    >
      <b-form @submit.prevent="submitInterest">
        <b-form-group
          label="Message to Agency"
          label-for="interest-message"
        >
          <b-form-textarea
            id="interest-message"
            v-model="interestMessage"
            placeholder="Introduce yourself and explain why you're interested in this opportunity..."
            rows="4"
            required
          />
        </b-form-group>
        <div class="d-flex justify-content-end mt-2">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-secondary"
            class="mr-1"
            @click="showInterestModal = false"
          >
            Cancel
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            type="submit"
          >
            Send Interest
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Contact Agency Modal -->
    <b-modal
      v-model="showContactModal"
      title="Contact Agency"
      hide-footer
      centered
    >
      <b-form @submit.prevent="sendContactMessage">
        <b-form-group
          label="Subject"
          label-for="contact-subject"
        >
          <b-form-input
            id="contact-subject"
            v-model="contactSubject"
            placeholder="Enter subject..."
            required
          />
        </b-form-group>
        <b-form-group
          label="Message"
          label-for="contact-message"
        >
          <b-form-textarea
            id="contact-message"
            v-model="contactMessage"
            placeholder="Type your message..."
            rows="4"
            required
          />
        </b-form-group>
        <div class="d-flex justify-content-end mt-2">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-secondary"
            class="mr-1"
            @click="showContactModal = false"
          >
            Cancel
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            type="submit"
          >
            Send Message
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BAvatar,
  BBadge,
  BOverlay,
  BAlert,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'BrokerDetails',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BAvatar,
    BBadge,
    BOverlay,
    BAlert,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      isLoading: false,
      jobId: null,
      jobDetails: {
        position: '',
        company: '',
        location: '',
        salaryMin: 0,
        salaryMax: 0,
        employmentType: '',
        datePosted: '',
        experienceYears: 0,
        description: '',
        requirements: [],
        responsibilities: [],
        agency: '',
        agencyLogo: null,
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        status: '',
        commissionPercentage: 0,
        isSaved: false,
      },
      matchedCandidates: [],
      showInterestModal: false,
      showContactModal: false,
      interestMessage: '',
      contactSubject: '',
      contactMessage: '',
    }
  },
  mounted() {
    this.jobId = this.$route.params.job_id
    this.loadJobDetails()
    this.loadMatchedCandidates()
  },
  methods: {
    async loadJobDetails() {
      this.isLoading = true
      try {
        const response = await this.$http.get(`/api/broker/jobs/${this.jobId}`)

        if (response.data.success) {
          const job = response.data.data
          this.jobDetails = {
            position: job.position,
            company: job.company?.name || 'N/A',
            location: `${job.working_location_state || ''}, ${job.working_location_country || ''}`.trim(),
            salaryMin: job.min_salary || 0,
            salaryMax: job.max_salary || 0,
            employmentType: job.employment_type ? job.employment_type.join(', ') : 'N/A',
            datePosted: job.date_posted,
            experienceYears: `${job.min_yoe || 0}-${job.max_yoe || 0}`,
            description: job.description || '',
            requirements: job.requirements ? job.requirements.split('\n').filter(r => r.trim()) : [],
            responsibilities: job.responsibilities ? job.responsibilities.split('\n').filter(r => r.trim()) : [],
            agency: job.agency?.name || 'Platform',
            agencyLogo: null,
            contactPerson: job.contact_person || 'N/A',
            contactEmail: job.contact_email || 'N/A',
            contactPhone: job.contact_phone || 'N/A',
            status: job.status,
            commissionPercentage: job.commission_percentage || 0,
            isSaved: job.is_saved || false,
          }
        }
      } catch (error) {
        this.$handleErrorResponse(error, 'Error', 'Failed to load job details', {
          showToast: true,
        })
      } finally {
        this.isLoading = false
      }
    },

    async loadMatchedCandidates() {
      try {
        const response = await this.$http.get(`/api/broker/jobs/${this.jobId}/matches`, {
          params: { refresh: false, min_score: 0 },
        })

        if (response.data.success) {
          this.matchedCandidates = response.data.data.matches.map(match => ({
            id: match.candidate_id,
            name: match.candidate_name,
            avatar: null,
            currentPosition: 'Candidate',
            matchScore: match.match_score,
            isAvailable: true,
            experienceYears: match.match_score_details?.years_of_experience || 0,
            expectedSalary: 0,
            skills: match.match_score_details?.matched_skills || [],
          }))
        }
      } catch (error) {
        console.error('Failed to load matched candidates:', error)
      }
    },

    goBack() {
      this.$router.push({ name: 'broker-list' })
    },

    async toggleSaveJob() {
      try {
        const response = await this.$http.post(`/api/broker/jobs/${this.jobId}/save`)

        if (response.data.success) {
          this.jobDetails.isSaved = response.data.data.is_saved
          this.$toastSuccess('Success', this.jobDetails.isSaved ? 'Job saved' : 'Job unsaved')
        }
      } catch (error) {
        this.$handleErrorResponse(error, 'Error', 'Failed to save job', {
          showToast: true,
        })
      }
    },

    expressInterest() {
      this.interestMessage = ''
      this.showInterestModal = true
    },

    async submitInterest() {
      try {
        const response = await this.$http.post(`/api/broker/jobs/${this.jobId}/interest`, {
          message: this.interestMessage,
        })

        if (response.data.success) {
          this.$toastSuccess('Success', 'Your interest has been sent to the agency')
          this.showInterestModal = false
        }
      } catch (error) {
        this.$handleErrorResponse(error, 'Error', 'Failed to submit interest', {
          showToast: true,
        })
      }
    },

    contactAgency() {
      this.contactSubject = `Regarding: ${this.jobDetails.position}`
      this.contactMessage = ''
      this.showContactModal = true
    },

    async sendContactMessage() {
      try {
        const response = await this.$http.post(`/api/broker/jobs/${this.jobId}/contact`, {
          subject: this.contactSubject,
          message: this.contactMessage,
        })

        if (response.data.success) {
          this.$toastSuccess('Success', 'Your message has been sent to the agency')
          this.showContactModal = false
        }
      } catch (error) {
        this.$handleErrorResponse(error, 'Error', 'Failed to send message', {
          showToast: true,
        })
      }
    },

    viewCandidateProfile(candidate) {
      console.log('View candidate profile:', candidate)
      this.$toastInfo('Info', 'Candidate profile view coming soon')
    },

    async submitCandidate(candidate) {
      const confirmed = await this.$simpleConfirmModal(
        'Submit Candidate',
        `Are you sure you want to submit ${candidate.name} for this position?`,
      )

      if (confirmed) {
        try {
          const response = await this.$http.post(`/api/broker/jobs/${this.jobId}/submit-candidate`, {
            candidateId: candidate.id,
            note: '',
            resumeFile: null,
          })

          if (response.data.success) {
            this.$toastSuccess('Success', `${candidate.name} has been submitted for this position`)
          }
        } catch (error) {
          this.$handleErrorResponse(error, 'Error', 'Failed to submit candidate', {
            showToast: true,
          })
        }
      }
    },

    getStatusVariant(status) {
      const variants = {
        active: 'success',
        filled: 'info',
        closed: 'secondary',
      }
      return variants[status] || 'secondary'
    },
  },
}
</script>

<style lang="scss" scoped>
.candidate-card {
  transition: all 0.3s ease;
  border: 1px solid #ebe9f1;

  &:hover {
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    transform: translateY(-5px);
  }
}
</style>
